import * as React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Stack,
    Skeleton,
    Paper,
    Box,
    Typography,
    Tooltip,
    FormControlLabel,
    Switch
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { DataGrid, GridColDef, GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { useEffect, useMemo, useState, useCallback } from "react";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectSettings } from "../../settings/settingsSlice";
import { Localized, useLocalization } from "@fluent/react";
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { loadClientDocumentTemplatesForStructure } from "./documentTemplateApi";
import { unwrapResult } from "@reduxjs/toolkit";
import { DocumentHeader } from "../documentsApi";
import { StructureName } from "./utils/StructureTreeView";
import { CancelButton } from "../../../app/components/CancelButton";
import { APIError } from "../../../app/appTypes";

type ClientDocumentTemplateSelectFieldProps = {
    structureId: number;
    documentType: string;
    value: string | number | null;
    onChange: (id: string) => void;
    onSelectTemplate?: (tpl: DocumentHeader | undefined) => void;
    showFiltersTab?: boolean;
    language?: string | null;
    filterStructureId?: number | null;
};

export const ClientDocumentTemplateSelectField: React.FC<ClientDocumentTemplateSelectFieldProps> = ({
    structureId,
    documentType,
    value,
    onChange,
    onSelectTemplate,
    showFiltersTab = false,
    language,
    filterStructureId
}) => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const { locale } = useAppSelector(selectSettings);

    const [open, setOpen] = useState(false);
    const [templates, setTemplates] = useState<DocumentHeader[]>([]);
    const [loading, setLoading] = useState(false);
    const [selection, setSelection] = useState<GridRowSelectionModel>(value ? [String(value)] : []);
    const [error, setError] = useState<APIError | undefined>(undefined);
    const [loadedParams, setLoadedParams] = useState<{ structureId: number; documentType: string } | null>(null);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], quickFilterValues: [] });
    const [languageCompatibleOnly, setLanguageCompatibleOnly] = useState(false);
    const [structureCompatibleOnly, setStructureCompatibleOnly] = useState(false);

    const quickFilterTerms = useMemo(
        () => (filterModel.quickFilterValues ?? [])
            .map((v) => String(v).trim())
            .filter(Boolean),
        [filterModel.quickFilterValues]
    );

    const renderHighlightedText = (value: unknown) => {
        const text = String(value ?? "");
        if (!text || quickFilterTerms.length === 0) {
            return <>{text}</>;
        }
        const escapedTerms = quickFilterTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
        const regex = new RegExp(`(${escapedTerms.join("|")})`  , "ig");
        const parts = text.split(regex);
        return (
            <>
                {parts.map((part, idx) => {
                    const isMatch = quickFilterTerms.some((term) => term.toLowerCase() === part.toLowerCase());
                    return isMatch ? (
                        <Box key={`${part}-${idx}`} component="mark">
                            <mark><b> {part}</b></mark>
                        </Box>
                    ) : (
                        <span key={`${part}-${idx}`}>{part}</span>
                    );
                })}
            </>
        );
    };

    const localeText = useMemo(() => {
        const en = enUS.components.MuiDataGrid.defaultProps.localeText;
        return locale === "pl"
            ? plPL.components.MuiDataGrid.defaultProps.localeText
            : en;
    }, [locale]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "no",
                headerName: l10n.getString("document-number", null, "_Nazwa"),
                width: 200,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "title",
                headerName: l10n.getString("document-title", null, "_title"),
                flex: 1,
                minWidth: 220,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "type",
                headerName: l10n.getString("document-type", null, "_type"),
                width: 140,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "structureId",
                headerName: l10n.getString("structure", null, "_structure"),
                minWidth: 200,
                renderCell: (params) => {
                    return <StructureName structureId={params.row.structureId} />;
                }
            },
            {
                field: "language",
                headerName: l10n.getString("language", null, "_language"),
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "isSystem",
                headerName: l10n.getString("system-item", null, "_systemItem"),
                type: "boolean",
                width: 120
            }
        ],
        [l10n, quickFilterTerms]
    );

    const selectedTemplate = useMemo(
        () => templates.find(r => String(r.id) === String(value)),
        [templates, value]
    );

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        setError(undefined);

        try {
            const docs = await dispatch(
                loadClientDocumentTemplatesForStructure({ structureId, documentType })
            ).then(unwrapResult);

            setTemplates(docs ?? []);
            setLoadedParams({ structureId, documentType });
        } catch (e) {
            setError(e as APIError);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    }, [dispatch, structureId, documentType]);

    const visibleTemplates = useMemo(() => {
        let rows = templates;
        if (showFiltersTab && languageCompatibleOnly && language) {
            const normalizedLanguage = language.trim().toLowerCase();
            rows = rows.filter((tpl) => String(tpl.language ?? "").trim().toLowerCase() === normalizedLanguage);
        }
        const hasFilterStructureId = showFiltersTab && filterStructureId !== undefined && filterStructureId !== null;
        if (showFiltersTab && structureCompatibleOnly && hasFilterStructureId) {
            rows = rows.filter((tpl) => String(tpl.structureId) === String(filterStructureId));
        }
        return rows;
    }, [templates, showFiltersTab, language, languageCompatibleOnly, structureCompatibleOnly, filterStructureId]);

    useEffect(() => {
        setSelection(value ? [String(value)] : []);
    }, [value]);

    useEffect(() => {
        const paramsChanged =
            loadedParams?.structureId !== structureId ||
            loadedParams?.documentType !== documentType;

        if (paramsChanged) {
            setTemplates([]);
            setLoadedParams(null);
        }
    }, [structureId, documentType, loadedParams]);

    useEffect(() => {
        const shouldLoadBecauseDialogOpened = open && templates.length === 0;
        const shouldLoadBecauseHasDefaultValue = !!value && templates.length === 0;

        if (!shouldLoadBecauseDialogOpened && !shouldLoadBecauseHasDefaultValue) {
            return;
        }

        loadTemplates();
    }, [open, value, templates.length, loadTemplates]);

    useEffect(() => {
        if (!value || templates.length === 0) return;

        const tpl = templates.find(t => String(t.id) === String(value));
        onSelectTemplate?.(tpl);
    }, [value, templates, onSelectTemplate]);

    const findById = (id: string | number) =>
        templates.find(r => String(r.id) === String(id));

    const confirmSelection = () => {
        const id = selection.length ? String(selection[0]) : "";

        if (id) {
            onChange(id);
            onSelectTemplate?.(findById(id));
        }

        setOpen(false);
    };

    const clearSelection = () => {
        setSelection([]);
        onChange("");
        onSelectTemplate?.(undefined);
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        setOpen(false);
    };

    if (loading && !open && templates.length === 0) {
        return <LoadingContainer />;
    }

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                {value ? (
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                ) : (
                    <PriorityHighIcon sx={{ color: "error.main", fontSize: 18 }} aria-hidden />
                )}
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: value ? "text.secondary" : "error.main" }}
                >
                    {value ? (
                        <Localized id="document-template-selected">
                            <span>_Wybrany szablon</span>
                        </Localized>
                    ) : (
                        <Localized id="document-template-select">
                            <span>_Wybierz szablon</span>
                        </Localized>
                    )}
                </Typography>
            </Stack>

            <Paper
                variant="outlined"
                onClick={() => setOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    borderColor: theme.palette.divider,
                    "&:hover": { borderColor: theme.palette.text.primary },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                })}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            <Localized id="name">
                                <span>_name</span>
                            </Localized>: {selectedTemplate?.title ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="type">
                                <span>_type</span>
                            </Localized>: {selectedTemplate?.type ?? "—"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value && (
                            <Tooltip
                                title={l10n.getString("clear-selected", null, "_Wyczyść wybór")}
                            >
                                <IconButton
                                    size="small"
                                    aria-label={l10n.getString("clear-selected", null, "_Wyczyść wybór")}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearSelection();
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip
                            title={l10n.getString("open-template-list", null, "_Otwórz listę szablonów")}
                        >
                            <IconButton
                                size="small"
                                aria-label={l10n.getString("open-template-list", null, "_Otwórz listę szablonów")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Localized id="document-template-select">
                        <span>_wybierz szablon</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    {loading ? (
                        <Skeleton variant="rectangular" height="60vh" />
                    ) : error ? (
                        <Box sx={{ p: 2 }}>
                            <Typography color="error">
                                <Localized id="document-template-cannot-load">
                                    <span>_Nie udało się załadować listy szablonów.
                                    </span>
                                </Localized>
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            {showFiltersTab && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        backgroundColor: (theme) => theme.palette.grey[50],
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={0.75}
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        justifyContent="space-between"
                                    >
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {l10n.getString("filters", null, "Filters")}
                                            </Typography>
                                        </Box>

                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={1.5}
                                            useFlexGap
                                            flexWrap="wrap"
                                            sx={{ columnGap: 2, rowGap: 0.5 }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={languageCompatibleOnly}
                                                        onChange={(_, checked) => setLanguageCompatibleOnly(checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={l10n.getString("urs-filter-language-compatible", null, "Only matching language")}
                                                sx={{ mr: 0, my: 0 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={structureCompatibleOnly}
                                                        onChange={(_, checked) => setStructureCompatibleOnly(checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={l10n.getString("urs-filter-structure-compatible", null, "Only matching structure")}
                                                sx={{ mr: 0, my: 0 }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Paper>
                            )}

                            <Box sx={{ height: "60vh", minHeight: 300, width: "100%" }}>
                                <DataGrid
                                    rows={visibleTemplates}
                                    columns={columns}
                                    localeText={localeText}
                                    density="compact"
                                    filterModel={filterModel}
                                    onFilterModelChange={setFilterModel}
                                    rowSelectionModel={selection}
                                    onRowSelectionModelChange={(model) => {
                                        const id = model.length ? String(model[0]) : "";
                                        setSelection(id ? [id] : []);
                                    }}
                                    slots={{ toolbar: QuickSearchToolbar }}
                                    initialState={{
                                        sorting: { sortModel: [{ field: "title", sort: "asc" }] },
                                    }}
                                />
                            </Box>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={confirmSelection}
                        disabled={!selection.length}
                    >
                        <Localized id="select">
                            <span>_Wybierz</span>
                        </Localized>
                    </Button>

                    <CancelButton loading={false} onClick={() => setOpen(false)} />
                </DialogActions>
            </Dialog>
        </>
    );
};